import React, { type FC, memo, useCallback, useState } from "react";
import clsx from "clsx";
import type { GradeBodyTypeType } from "../../../../../utils/types/Model";
import EngineSpecs from "./EngineSpecs";
import {
    type MotorizationItemType,
    getFullEngineCode,
    getMotorizationData,
    getMotorizations,
    getUniqueMotorizationId,
} from "../../../../../utils/gradeUtils";
import { trackEngineOptionsClose, trackEngineOptionsOpen, trackSelectEngine } from "../../../../../utils/tracking";
import styles from "./EngineInfo.module.scss";
import { useGradeSelectorDispatch, useGradeSelectorSelector } from "../../../../../redux/hooks";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import EngineSelect from "./EngineSelect";
import { getUniqueGradeBodyTypeId } from "../../../../../../../../common-deprecated/features/apheleia/utils/aphUtils";
import { setSelectedMotorization as selectMotorization } from "../../../../../redux/slices/GradeSelectorModelSlice";

const Specs: FC<{ gradeBodyType: GradeBodyTypeType }> = ({ gradeBodyType }) => {
    const dispatch = useGradeSelectorDispatch();

    const modelId = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.id);
    const modelName = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.name);
    const activeFilters = useGradeSelectorSelector((state) => state.gradeSelectorFilters.filters);

    const [isOpen, setIsOpen] = useState(false);

    const motorizations = getMotorizations(gradeBodyType, activeFilters);

    const hasMultipleMotorizations = motorizations.length > 1;

    const selectedMotorization = getMotorizationData(gradeBodyType);
    const uniqueBodyTypeId = getUniqueGradeBodyTypeId(
        gradeBodyType.grade.id,
        gradeBodyType.bodyType.id,
        gradeBodyType.submodel?.id,
    );

    const carGradeName = `${modelName} ${gradeBodyType.grade.name} ${gradeBodyType.bodyType.name}`;

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                trackEngineOptionsClose(carGradeName);
            } else {
                trackEngineOptionsOpen(carGradeName);
            }
        },
        [carGradeName],
    );

    const selectMotorizationCallback = useCallback(
        (motorization: MotorizationItemType) => {
            trackSelectEngine(
                carGradeName,
                getFullEngineCode(motorization.engine, motorization.transmission, motorization.wheeldrive),
            );
            dispatch(
                selectMotorization({
                    uniqueBodyTypeId,
                    motorization: {
                        engineId: motorization.engine.id,
                        transmissionId: motorization.transmission.id,
                        wheeldriveId: motorization.wheeldrive.id,
                    },
                }),
            );
        },
        [carGradeName, dispatch, uniqueBodyTypeId],
    );

    return (
        <div className={styles.motorization}>
            <Collapse
                disabled={!hasMultipleMotorizations}
                open={isOpen}
                onClick={() => hasMultipleMotorizations && setIsOpen(!isOpen)}
                onChange={() => onOpenChange(isOpen)}
                className={clsx(styles.collapse, isOpen && styles.open, !hasMultipleMotorizations && styles.disabled)}
                summary={
                    <EngineSelect
                        props={{
                            motorization: selectedMotorization,
                            modelId,
                            summary: true,
                            availableForOnlineSales: gradeBodyType.grade.availableForOnlineSales,
                        }}
                    />
                }
            >
                {motorizations.map((motorization) => {
                    const id = getUniqueMotorizationId(
                        motorization.engine,
                        motorization.transmission,
                        motorization.wheeldrive,
                    );
                    return (
                        <EngineSelect
                            key={id}
                            props={{
                                motorization,
                                modelId,
                                setSelectedMotorization: selectMotorizationCallback,
                                active:
                                    getUniqueMotorizationId(
                                        selectedMotorization.engine,
                                        selectedMotorization.transmission,
                                        selectedMotorization.wheeldrive,
                                    ) === id,
                                showSpecs: true,
                                availableForOnlineSales: gradeBodyType.grade.availableForOnlineSales,
                            }}
                        />
                    );
                })}
            </Collapse>

            <EngineSpecs
                showPrice
                engine={selectedMotorization.engine}
                transmission={selectedMotorization.transmission}
                wheeldrive={selectedMotorization.wheeldrive}
                selectedColourId={gradeBodyType.selectedColourId}
            />
        </div>
    );
};

export default memo(Specs);
